import {
  getGasAnalysisIndicators,
  getGasBandsChart,
} from "$/api/market-monitoring/gas-analysis.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import ComposedAreaGraph from "$/lib/components/graphs/ComposedAreaGraph";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import { RadioButtonSet } from "$/lib/components/molecules/RadioButtonSet";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import {
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import { handleOnChangeDefaultGraphsTabs } from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import {
  cn,
  getArrayKeys,
  getMinMax,
} from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import ProductIndicator from "../../ProductIndicator";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";

const COLORS: Record<string, string> = {
  "Borne Supérieure": "#d45359",
  "Borne Inférieure": "#64a34b",
  "Borne Supérieure_display": "#d45359",
  "Borne Inférieure_display": "#64a34b",
  // "TTF - Borne Supérieure": "#d45359",
  // "TTF - Borne Inférieure": "#64a34b",
  "PEG - Cal +1": "#2a6d34",
  "PEG - Cal +2": "#65a551",
  "PEG - Cal +3": "#b1c456",
  "TTF - Cal +1": "#a3013a",
  "TTF - Cal +2": "#e56453",
  "TTF - Cal +3": "#f09883",
};

type Props = {
  selectedDate: Date;
} & CustomDashboardProps;

const GasBandsChart = ({
  selectedDate,
  id,
  isDraggable = false,
  isShowcase = false,
}: Props) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const [filters, setFilters] = useState<"peg" | "ttf">("peg");

  const [dateDiff, setDateDiff] = useState(1);

  const { data: indicatorsData } = useQuery({
    queryKey: ["get-gas-analysis-indicators", selectedDate, dateDiff],
    queryFn: () =>
      getGasAnalysisIndicators({
        date: getDate(selectedDate),
        dateDiff,
      }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["get-gas-bands-chart", dateRange, dateDiff],
    queryFn: () =>
      getGasBandsChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
        dateDiff,
      }),
  });

  const indicatorValues1 = useMemo(() => {
    if (indicatorsData) {
      if (filters === "peg")
        return [
          indicatorsData.indicator1,
          indicatorsData.indicator2,
          indicatorsData.indicator3,
        ];
      else
        return [
          indicatorsData.indicator4,
          indicatorsData.indicator5,
          indicatorsData.indicator6,
        ];
    }

    return [];
  }, [indicatorsData, filters]);

  // const indicatorValues2 = useMemo(() => {
  //   if (indicatorsData) {
  //     return [
  //       indicatorsData.indicator4,
  //       indicatorsData.indicator5,
  //       indicatorsData.indicator6,
  //     ];
  //   }

  //   return [];
  // }, [indicatorsData]);

  const toDisplay = useMemo(
    () =>
      data
        ? filters === "peg"
          ? data.pegProducts
          : filters === "ttf"
            ? data.ttfProducts
            : []
        : [],
    [data, filters],
  );

  const keys = useMemo(
    () => [
      "Borne Supérieure",
      "Borne Inférieure",
      ...getArrayKeys(toDisplay, "date"),
    ],
    [toDisplay],
  );

  const result = useMemo(() => getMinMax(toDisplay, keys), [keys, toDisplay]);

  const dataToBeDisplayed = useMemo(
    () =>
      toDisplay.map((item) => ({
        ...item,
        ...(item["Borne Supérieure_display"] && {
          "Borne Supérieure": [
            item["Borne Supérieure_display"],
            result.max.value,
          ] as [number, number],
        }),
        ...(item["Borne Inférieure_display"] && {
          "Borne Inférieure": [
            result.min.value,
            item["Borne Inférieure_display"],
          ] as [number, number],
        }),
      })),
    [toDisplay, result],
  );

  const lineKey = useMemo(
    () =>
      filters === "peg" ? `PEG - Cal +${dateDiff}` : `TTF - Cal +${dateDiff}`,
    [dateDiff, filters],
  );

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  const colors: Record<string, string> = useMemo(
    () =>
      keys.reduce(
        (acc, key) => ({
          ...acc,
          [key]: COLORS[key],
        }),
        {},
      ),
    [keys],
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    if (!data) return;
    setGraphVisibility(
      keys
        .filter((key) => !key.includes("display"))
        .map((key) => ({
          name: key,
          // isVisible: true,
          color: COLORS[key],
        })),
    );
  }, [keys, filters, data]);

  return (
    <DisplayBox
      title="Bandes de Bollinger - Calendaires"
      Icon={LineChartIcon}
      theme="green"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer>
        <Flexbox fullWidth fullHeight className="gap-6 rounded-xl">
          <Flexbox fullWidth>
            <ProductIndicator
              type="GAS"
              indicatorValues={indicatorValues1}
              hasChart={false}
              hasDatePicker={false}
              asMain={false}
              containerClassName={cn(
                "rounded-xl",
                // filters.peg && filters.ttf && "rounded-t-xl rounded-b-none",
              )}
              itemClassName="rounded-xl"
            />
            {/* {filters.peg && filters.ttf && (
              <ProductIndicator
                type="GAS"
                indicatorValues={indicatorValues2}
                hasChart={false}
                hasDatePicker={false}
                asMain={false}
                containerClassName="rounded-xl rounded-b-xl rounded-t-none pt-0"
                itemClassName="rounded-xl"
              />
            )} */}
          </Flexbox>

          {isLoading ? (
            <Flexbox fullWidth fullHeight align="center" justify="center">
              <Loader height={40} width={40} />
            </Flexbox>
          ) : (
            <ComposedAreaGraph
              data={dataToBeDisplayed}
              xDataKey="date"
              areaKeys={["Borne Supérieure", "Borne Inférieure"]}
              hideKeys={["Borne Supérieure", "Borne Inférieure"]}
              notVisibleKeys={[
                "Borne Supérieure_display",
                "Borne Inférieure_display",
              ]}
              lineKey={lineKey}
              colors={colors}
              displayNames={{
                "Borne Supérieure_display": "Borne Supérieure",
                "Borne Inférieure_display": "Borne Inférieure",
              }}
              theme="blue"
              leftMargin={-10}
              heightPercent={70}
              domain={[result.min.value, result.max.value]}
              areaChartsFillOpacity={0.4}
            />
          )}
        </Flexbox>

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
            theme="green"
          />

          <RadioButtonSet<"peg" | "ttf">
            id="gas-analysis-bands-chart"
            title="Marché"
            selectedId={filters}
            onChange={(id) => setFilters(id)}
            options={[
              { id: "peg", label: "PEG" },
              { id: "ttf", label: "TTF" },
            ]}
          />

          <RadioButtonSet<number>
            id="gas-analysis-date-diffF"
            title="Produit calendaire"
            selectedId={dateDiff}
            onChange={(id) => setDateDiff(id)}
            options={[
              { id: 1, label: "Cal +1" },
              { id: 2, label: "Cal +2" },
              { id: 3, label: "Cal +3" },
            ]}
          />

          <GraphFilter
            title="Légende"
            data={graphVisibility}
            handleOnChange={handleOnChangeGraphVisiblity}
          />
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default GasBandsChart;
